/**
 * Cocoon 
 *
 * Interactive. Rotate and zoom with mouse or fingers. Can you unravel this little crustacean creature?
 * It likes to curl up tight, but if you're patient you can unravel it. Sold "as is", tested on a variety of browsers/devices, but won't work on all.
 *
 * creative coding, interactive, 3d, creature, shell
 * 
 */

import * as THREE from 'three/build/three.module.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

import chroma from 'chroma-js';

import World from '../common/World';
import WorldConfig from '../common/WorldConfig';

// Check out roughness mipmapper, sounds cool
// https://stackoverflow.com/questions/60704912/play-a-gltf-animation-three-js
// Prototypes etc.
Number.prototype.clamp = function(min, max) {
  return Math.min(Math.max(this, min), max);
};

function convertHex(hex) {
	return parseInt(hex.replace(/^#/, ''), 16);
}

const LAYERS = 25;
const SLICES = 6;

class Main extends World {

	constructor() {


		super({
			cameraType: 'orbit',
			useCameraLight: false,
			ambientIntensity: .5,
			backgroundColor: 0x000000,
			useBloom: true,
			mutedTone: false,
			bloomIntensity: .2
		});

		const promises = [
			this.processGLTF(require('url:./cocoon.glb'), {
				addAnimations: false,			
			})
		];

		this.scale = chroma.scale([0xFF0000, 0x00FF00]);

		this.startScale = chroma.scale([0xFF0000, 0xDD0022, 0x990066, 0x5511AA, 0x992299, 0xFF0000]);
		this.endScale = chroma.scale([0x00FF00, 0x22AAFF, 0x33FF99, 0x66FF33, 0x997744, 0x00FF00]);

		this.cameraControls.minDistance = 1.5;
		this.cameraControls.maxDistance = 5;
		this.cameraControls.distance = 2.5;

		this.cameraControls.maxPolarAngle = Infinity;
		this.cameraControls.minPolarAngle = -Infinity;

		this.cameraControls.truckSpeed = 0;

		this.cameraControls.mouseButtons.right = false;

		this.camera.near = 0.25;
		this.camera.far = 250;
		this.camera.updateProjectionMatrix();


		this.cams = [];
		this.allCams = [];

		this.radius = 25;
		this.total = 128;
		this.center = new THREE.Vector2(0, 0);
		this.duration = 5;

		this.rotationSpeed = 1;
		this.rotationOffset = 0;

		const pivotMaterial = new THREE.MeshBasicMaterial({ colorWrite: false });
		const pivotGeometry = new THREE.BoxGeometry( .01, .01, .01 );
	
		this.layers = [];

		Promise.all(promises).then(v => {

			const data = v[0];

			const slice = this.scene.getObjectByName("Slice");
			
			
			const slicePivot = new THREE.Mesh(pivotGeometry, pivotMaterial);
			//this.scene.add(slicePivot);



			for(let x = 0; x < SLICES; x++) {
				const clone = slice.clone(true);
				//console.log(this.scale(x / SLICES).hex());
				//clone.material = clone.material.clone();
				//clone.material.color = new THREE.Color(this.scale(x / SLICES).hex());
				//console.log(clone.material.color);
				
				slicePivot.add(clone);

			}


			for(let y = 0; y <= LAYERS; y++) {
				const clone = slicePivot.clone(true);
				const color = this.scale(y / LAYERS);
				const colorScale = chroma.scale([color, color.darken(2)]);
				clone.children.forEach((child, i) => {
					const childScale = 1 - (i / (SLICES * 32));
					child.scale.set(childScale, childScale, childScale);
					child.material = child.material.clone();
					child.material.color = new THREE.Color(colorScale(i / SLICES).hex());
					child.rotation.y = (Math.PI / SLICES * i) * (y / LAYERS);

				});
				const scale = 1 - ((1/LAYERS) * y);
				clone.scale.set(scale, scale, scale);
				clone.rotation.y = clone.rotation.y + y * Math.PI / LAYERS;
				clone.i = y;
				this.scene.add(clone);
				this.layers.push(clone);
			}


			slice.position.y = -999999;
			
			const myobj = document.getElementById("loading");
			myobj.remove(); 


		});

	}


	renderLoop(t) {
		super.renderLoop(t);

		const po = this.cameraControls.polarAngle;
		const az = this.cameraControls.azimuthAngle;

		//console.log(this.azimuthSpeed);

		this.rotationSpeed += this.azimuthSpeed * this.delta * 5;

		this.rotationSpeed = this.rotationSpeed.clamp(-2, 2);

		let updateColor = false;
		if(this.frame % 5 == 0) {
			updateColor = true;
		}

		if(updateColor) {

			const pos = (t / 30000) % 30000 % 1;

			//console.log(pos);

			const start = this.startScale(pos);
			const end = this.endScale(pos)
			this.scale = chroma.scale([start, end]);
		}

		this.layers.forEach((layer, i) => {

			layer.rotation.x = (layer.i / LAYERS * 2) * po;
			layer.rotation.y += (this.delta * (1 + layer.i / LAYERS)) * this.rotationSpeed;

			if(this.frame % 5 == 0) {
				const color = this.scale(i / LAYERS);
				const childColorScale = chroma.scale([color, color.darken(2)]);
				layer.children.forEach((child, j) => {

					child.material.color = new THREE.Color(childColorScale(j / SLICES).hex());
				});
			}

		});

	}


}

export default Main;